<template>
  <div class="settings-page">
    <div class="studio-page-header">
      <div>
        <h2>Studio</h2>
        <div class="subtitle">
          Canvases: screens composed of widgets, built for a specific purpose.
        </div>
      </div>
      <BaseButton
        v-if="!canvasIssue"
        kind="main"
        type="primary"
        :text="isCreating ? 'Creating…' : 'New'"
        :state="isCreating ? 'loading' : 'default'"
        @click="createCanvas"
      >
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </div>

    <!-- Canvases live per workspace: a selector when the organization has
         several (defaulting to the default workspace). -->
    <div v-if="workspaceOptions.length > 1" class="studio-toolbar">
      <BaseInputSelect
        class="workspace-select"
        :value="workspaceId ?? ''"
        :options="workspaceOptions"
        select-only
        @selected="selectWorkspace($event.value)"
      />
    </div>

    <!-- Same action either way (the initialize ladder), but the framing
         differs: "missing" is a feature to enable, "mismatch" a broken
         type to repair. -->
    <div v-if="canvasIssue === 'missing'" class="studio-repair">
      <p>Canvases are not enabled in this workspace yet.</p>
      <BaseButton
        kind="main"
        type="primary"
        :text="isRepairing ? 'Enabling…' : 'Enable'"
        :state="isRepairing ? 'loading' : 'default'"
        @click="repair"
      />
      <p v-if="repairFailed" class="error">
        Enabling canvases failed. Please try again.
      </p>
    </div>
    <div v-else-if="canvasIssue === 'mismatch'" class="studio-repair">
      <p>
        The Canvas type of this workspace does not have the expected schema.<br />Click
        repair to restore it.
      </p>
      <BaseButton
        kind="main"
        type="primary"
        :text="isRepairing ? 'Repairing…' : 'Repair'"
        :state="isRepairing ? 'loading' : 'default'"
        @click="repair"
      />
      <p v-if="repairFailed" class="error">
        The repair failed. You can fix it manually by editing the Canvas type so
        it has a "Title" text property (used as the title field) and a "Body"
        json property.
      </p>
    </div>
    <div v-else-if="canvasRows.length === 0" class="studio-empty">
      <p>No canvases yet. Create one to get started.</p>
    </div>
    <div v-else class="canvas-list">
      <button
        v-for="row in canvasRows"
        :key="row.id"
        class="canvas-row"
        type="button"
        @click="openCanvas(row.id)"
      >
        <span class="emoji">🖼️</span>
        <span class="name">{{ row.title || "Untitled" }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRef } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import {
  runGraphCommand,
  unwrapGraphCommandResult,
} from "@/components/graph/command";
import { resolveProvisionedCanvas } from "@/components/graph/provisioned";
import { asGraphRecordResult, asString } from "@/components/graph/result";
import { extractTitleFromGraphResult } from "@/components/graph/title";
import AddIcon from "@/components/icons/AddIcon.vue";
import * as inject from "@/components/inject";
import { getOrganization } from "@/composables/useProfile";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";
import { mapIfDefined } from "@/utils/undefined";

type CanvasRow = {
  id: string;
  title: string;
};

/**
 * Organization Studio: lists the canvases of one of the organization's
 * workspaces (a selector when there are several, defaulting to the default
 * workspace) and creates new ones. Canvases are rows of the workspace's
 * `canvases` database (created on demand by `useCanvases`).
 */
export default defineComponent({
  name: "SettingsStudio",
  components: { AddIcon, BaseButton, BaseInputSelect },
  props: {
    orgId: { type: String, required: true },
  },
  async setup(props) {
    const orgId = toRef(props, "orgId");
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const canvases = inject.canvases();
    const logger = inject.logger("SettingsStudio");
    const releaser = useReleaser();

    // Canvases live per workspace. The screen shows one workspace at a
    // time: the user's pick when it belongs to this organization (also the
    // fallback when the org changes under a stale pick), otherwise the
    // default workspace (the personal workspace for the personal
    // organization).
    const workspaces = computed(
      () => getOrganization(profile.value, orgId.value)?.workspaces ?? []
    );
    const selectedWorkspaceId = ref<string | undefined>(undefined);
    const workspaceId = computed(() => {
      const list = workspaces.value;
      const selected = list.find((w) => w.id === selectedWorkspaceId.value);
      return (selected ?? list.find((w) => w.default) ?? list[0])?.id;
    });
    const selectWorkspace = (id: string) => {
      selectedWorkspaceId.value = id;
    };
    // Default workspace first, then the rest alphabetically (the TeamTasks
    // selector's order).
    const workspaceOptions = computed<SelectOption[]>(() =>
      [...workspaces.value]
        .sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          return a.name.localeCompare(b.name);
        })
        .map((w) => ({ value: w.id, label: w.name, aliases: [] }))
    );

    const canvasResolution = computed(() => {
      const databases = workspaceId.value
        ? workspaceDatabases.value?.[workspaceId.value]
        : undefined;
      return databases ? resolveProvisionedCanvas(databases) : undefined;
    });
    const databaseId = computed(() => canvasResolution.value?.database?.id);

    // No designated Canvas database once the catalog is loaded: "missing"
    // (never provisioned — the empty state offers to Enable) or "mismatch"
    // (a Canvas type exists but was edited/broken — it offers to Repair).
    // Both actions run the reuse → repair → recreate ladder of
    // `useCanvases.initialize`; the catalog then ticks reactively and the
    // screen falls back to the regular list.
    const canvasIssue = computed(() => canvasResolution.value?.issue);

    const isRepairing = ref(false);
    const repairFailed = ref(false);

    const repair = async () => {
      if (isRepairing.value) return;
      const wsId = workspaceId.value;
      if (!wsId) return;
      isRepairing.value = true;
      repairFailed.value = false;
      try {
        const dbId = (await canvases.value?.initialize(wsId)) ?? null;
        if (!dbId) repairFailed.value = true;
      } catch (error) {
        logger.error("failed to repair the Canvas type", error);
        repairFailed.value = true;
      } finally {
        isRepairing.value = false;
      }
    };

    // Live rows query of the canvases database; rebinds when the database
    // appears (e.g. right after the first "New" created it).
    const rowsRef = releaser.add(
      await bindAsync(databaseId, async (dbId) => {
        const wsId = workspaceId.value;
        if (!dbId || !wsId) return releasableRef(undefined);
        const compiledRef = await controller.graph.query.compile(
          wsId,
          `databases.["${dbId}"].rows`,
          {}
        );
        const compiled = compiledRef.release();
        if (!compiled || "error" in compiled) {
          logger.error("unable to compile the canvases query", { compiled });
          return releasableRef(undefined);
        }
        return controller.graph.query.run(compiled.value);
      })
    );

    const canvasRows = computed<CanvasRow[]>(() => {
      const result = rowsRef.value;
      if (!result || "error" in result || !(result.value instanceof Map)) {
        return [];
      }
      const rows: CanvasRow[] = [];
      for (const [key, raw] of result.value.entries()) {
        const record = asGraphRecordResult(raw);
        if (!record) continue;
        const rowId =
          mapIfDefined(asString)(
            mapIfDefined(asGraphRecordResult)(record._routine)?.id
          ) ?? (typeof key === "string" ? key : undefined);
        if (!rowId) continue;
        rows.push({
          id: rowId,
          title: extractTitleFromGraphResult(record) ?? "",
        });
      }
      return rows.sort((a, b) => a.title.localeCompare(b.title));
    });

    const openCanvas = (rowId: string) => {
      const wsId = workspaceId.value;
      if (!wsId) return;
      void navigateTo(RouteNames.Canvas, router, {
        params: { orgId: orgId.value, workspaceId: wsId, rowId },
      });
    };

    const isCreating = ref(false);

    const createCanvas = async () => {
      if (isCreating.value) return;
      const wsId = workspaceId.value;
      if (!wsId) return;
      isCreating.value = true;
      try {
        // Ensure the canvases database exists (first canvas of a workspace).
        const dbId =
          databaseId.value ?? (await canvases.value?.initialize(wsId)) ?? null;
        if (!dbId) {
          throw new Error("the canvases database could not be resolved");
        }
        const command = unwrapGraphCommandResult(
          await controller.graph.command.compile(
            wsId,
            "databases.[dbId].rows add { 'Title': title }",
            { dbId, title: "Untitled" }
          ),
          "compile"
        );
        const rowId = String(await runGraphCommand(controller, command));
        // Open the fresh canvas directly in edit mode.
        await navigateTo(RouteNames.Canvas, router, {
          params: { orgId: orgId.value, workspaceId: wsId, rowId },
          query: { edit: "1" },
        });
      } catch (error) {
        logger.error("failed to create a canvas", error);
      } finally {
        isCreating.value = false;
      }
    };

    return {
      canvasIssue,
      canvasRows,
      createCanvas,
      isCreating,
      isRepairing,
      openCanvas,
      repair,
      repairFailed,
      selectWorkspace,
      workspaceId,
      workspaceOptions,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.studio-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  h2 {
    margin-bottom: 4px;
  }

  .subtitle {
    margin: 0;
  }

  // The flex layout would otherwise shrink the button below its content
  // width and ellipsize its label ("+ N…").
  .base-button {
    flex-shrink: 0;
  }
}

.studio-toolbar {
  margin-bottom: 16px;

  .workspace-select {
    max-width: 260px;
  }
}

.studio-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  p {
    margin: 0;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-50);
    }
  }
}

.studio-repair {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: 200px;

  p {
    margin: 0;
    text-align: center;
    max-width: 480px;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-50);
    }
  }

  .error {
    @include themed() {
      color: t($secondary-50);
    }
  }

  // BaseButton vertically centers itself with auto margins, which would
  // consume the free space and push the message to the top.
  .base-button {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.canvas-list {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
}

.canvas-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: start;
  @include transition(background-color 0.15s ease);

  .emoji {
    font-size: 16px;
    line-height: 16px;
  }

  .name {
    @extend %font-caption-medium;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include themed() {
      color: t($grey-90);
    }
  }

  @include themed() {
    &:hover {
      background: t($grey-05);
    }
  }
}
</style>
